/**
 * 连接基础URL和键值，生成完整路径
 * @param {string} baseURL - 基础URL路径
 * @param {string} key - 键值路径
 * @returns {string} 完整路径
 */
function joinKey(baseURL, key) {
  if (!key) key = "/";
  if (!baseURL) return key.startsWith("/") ? key : "/" + key;
  const a = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
  const b = key.startsWith("/") ? key : "/" + key;
  return a + b;
}

/**
 * 检测浏览器是否支持 Cache API
 * @returns {boolean} 是否支持
 */
function isCacheAPISupported() {
  try {
    return typeof self !== "undefined" && "caches" in self;
  } catch (_) {
    return false;
  }
}

/**
 * 检测浏览器是否支持 IndexedDB
 * @returns {boolean} 是否支持
 */
function isIDBSupported() {
  try {
    return typeof indexedDB !== "undefined";
  } catch (_) {
    return false;
  }
}

/**
 * 将各种类型的数据转换为 Response 对象
 * @param {*} value - 要转换的值（可以是 Response、Blob、ArrayBuffer、string 或其他）
 * @param {string} contentType - 内容类型
 * @returns {Response} Response 对象
 */
function toResponse(value, contentType) {
  if (value instanceof Response) return value;
  if (value instanceof Blob) return new Response(value, { headers: { "Content-Type": value.type || contentType || "application/octet-stream" } });
  if (value instanceof ArrayBuffer) return new Response(value, { headers: { "Content-Type": contentType || "application/octet-stream" } });
  if (typeof value === "string") return new Response(new Blob([value], { type: contentType || "text/plain;charset=utf-8" }));
  return new Response(new Blob([JSON.stringify(value)], { type: contentType || "application/json" }));
}

/**
 * Cache API 后端实现
 * 使用浏览器的 Cache API 存储数据
 */
class CacheBackend {
  /**
   * 构造函数
   * @param {string} name - 缓存名称
   * @param {string} baseURL - 基础URL路径
   */
  constructor(name, baseURL) {
    this.kind = "cache";
    this.name = name;
    // 确保 baseURL 有一个明确的路径前缀
    this.baseURL = baseURL || `/${name}`;
    this.ready = caches.open(name);
  }

  /**
   * 生成完整的键值路径
   * @private
   * @param {string} key - 键值
   * @returns {string} 完整路径
   */
  _fullKey(key) {
    return joinKey(this.baseURL, key);
  }

  /**
   * 存储数据
   * @param {string} key - 键值
   * @param {*} value - 要存储的值
   * @param {string} contentType - 内容类型
   */
  async put(key, value, contentType) {
    const c = await this.ready;
    await c.put(this._fullKey(key), toResponse(value, contentType));
  }

  /**
   * 获取数据
   * @param {string} key - 键值
   * @param {string} as - 返回数据的格式 ('text'、'json'、'blob' 或 'response')
   * @returns {Promise<*>} 返回指定格式的数据
   */
  async get(key, as) {
    const c = await this.ready;
    const res = await c.match(this._fullKey(key));
    if (!res) return undefined;
    switch (as) {
      case "text":
        return res.text();
      case "json":
        return res.json().catch(() => undefined);
      case "blob":
        return res.blob();
      default:
        return res;
    }
  }

  /**
   * 检查键值是否存在
   * @param {string} key - 键值
   * @returns {Promise<boolean>} 是否存在
   */
  async has(key) {
    const c = await this.ready;
    return !!(await c.match(this._fullKey(key)));
  }

  /**
   * 删除指定键值的数据
   * @param {string} key - 键值
   * @returns {Promise<boolean>} 是否删除成功
   */
  async delete(key) {
    const c = await this.ready;
    return c.delete(this._fullKey(key));
  }

  /**
   * 列出所有键值
   * @returns {Promise<string[]>} 键值列表
   */
  async list() {
    const c = await this.ready;
    const reqs = await c.keys();
    const base = this.baseURL ? (this.baseURL.endsWith("/") ? this.baseURL.slice(0, -1) : this.baseURL) : "";
    return reqs.map(r => new URL(r.url, location.origin).pathname)
      .filter(p => (base ? p === base || p.startsWith(base + "/") : true))
      .map(p => (base ? p.slice(base.length) || "/" : p));
  }

  /**
   * 清空所有数据
   */
  async clear() {
    await caches.delete(this.name);
    this.ready = caches.open(this.name);
  }
}

/**
 * IndexedDB 后端实现
 * 使用浏览器的 IndexedDB 存储数据
 */
class IDBBackend {
  /**
   * 构造函数
   * @param {string} name - 数据库名称
   * @param {string} baseURL - 基础URL路径
   */
  constructor(name, baseURL) {
    this.kind = "idb";
    this.name = name;
    // 确保 baseURL 有一个明确的路径前缀
    this.baseURL = baseURL || `/${name}`;
    this.store = "files";
    this.dbp = new Promise((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.store)) db.createObjectStore(this.store);
      };
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    });
  }

  /**
   * 生成完整的键值路径
   * @private
   * @param {string} key - 键值
   * @returns {string} 完整路径
   */
  _fullKey(key) {
    return joinKey(this.baseURL, key);
  }

  /**
   * 获取对象存储
   * @private
   * @param {string} mode - 事务模式 ('readonly' 或 'readwrite')
   * @returns {Promise<IDBObjectStore>} 对象存储
   */
  async _os(mode) {
    const db = await this.dbp;
    return db.transaction(this.store, mode).objectStore(this.store);
  }

  /**
   * 存储数据
   * @param key
   * @param value
   * @param contentType
   * @returns {Promise<void>}
   */
  async put(key, value, contentType) {
    const os = await this._os("readwrite");
    let blob;
    if (value instanceof Blob) blob = value;
    else if (value instanceof ArrayBuffer) blob = new Blob([value], { type: contentType || "application/octet-stream" });
    else if (typeof value === "string") blob = new Blob([value], { type: contentType || "text/plain;charset=utf-8" });
    else blob = new Blob([JSON.stringify(value)], { type: contentType || "application/json" });
    await new Promise((resolve, reject) => {
      const req = os.put(blob, this._fullKey(key));
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * 获取数据
   * @param key
   * @param as
   * @returns {Promise<Response|undefined|*>}
   */
  async get(key, as) {
    const os = await this._os("readonly");
    const blob = await new Promise((resolve, reject) => {
      const req = os.get(this._fullKey(key));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    if (!blob) return undefined;
    switch (as) {
      case "blob":
        return blob;
      case "text":
        return blob.text();
      case "json":
        return JSON.parse(await blob.text());
      default:
        return new Response(blob, { headers: { "Content-Type": blob.type || "application/octet-stream" } });
    }
  }

  /**
   * 检查键值是否存在
   * @param key
   * @returns {Promise<unknown>}
   */
  async has(key) {
    const os = await this._os("readonly");
    return new Promise((resolve, reject) => {
      const req = os.getKey(this._fullKey(key));
      req.onsuccess = () => resolve(req.result !== undefined);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * 删除指定键值的数据
   * @param key
   * @returns {Promise<unknown>}
   */
  async delete(key) {
    const os = await this._os("readwrite");
    return new Promise((resolve, reject) => {
      const req = os.delete(this._fullKey(key));
      req.onsuccess = () => resolve(true);
      req.onerror = () => reject(req.error);
    });
  }

  /**
   * 列出所有键值
   * @returns {Promise<*[]>}
   */
  async list() {
    const os = await this._os("readonly");
    const keys = [];
    const base = this.baseURL ? (this.baseURL.endsWith("/") ? this.baseURL.slice(0, -1) : this.baseURL) : "";
    await new Promise((resolve, reject) => {
      const req = os.openKeyCursor();
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cur = req.result;
        if (cur) {
          const k = String(cur.key);
          if (!base || k === base || k.startsWith(base + "/")) keys.push(base ? k.slice(base.length) || "/" : k);
          cur.continue();
        } else resolve();
      };
    });
    return keys;
  }

  /**
   * 清空所有数据
   */
  async clear() {
    const os = await this._os("readwrite");
    await new Promise((resolve, reject) => {
      const req = os.clear();
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }
}

/**
 * 缓存文件存储主类
 * 提供统一的接口，可以使用 Cache API 或 IndexedDB 作为后端
 */
export default class CacheFileStore {
  /**
   * 构造函数
   * @param {string} opts - 配置选项
   * @param {string} opts.name - 缓存名称，默认为 'domain-cache'
   * @param {string} opts.baseURL - 基础URL路径
   * @param {string} opts.backend - 默认后端 ('cache' 或 'idb')
   * @param {string[]} opts.readOrder - 读取时的后端优先顺序
   */
  constructor(opts) {
    opts = opts || {};
    this.name = opts.name || "domain-cache";
    this.baseURL = opts.baseURL;
    this.backends = {};
    if (isCacheAPISupported()) this.backends.cache = new CacheBackend(this.name, this.baseURL);
    if (isIDBSupported()) this.backends.idb = new IDBBackend(this.name, this.baseURL);
    this.defaultBackend = opts.backend || (this.backends.cache ? "cache" : "idb");
    this.readOrder = opts.readOrder || (this.backends.cache ? ["cache", "idb"] : ["idb"]);
  }

  /**
   * 选择指定的后端
   * @private
   * @param {string} backend - 后端名称
   * @returns {CacheBackend|IDBBackend} 后端实例
   * @throws {Error} 如果后端不可用
   */
  _pick(backend) {
    const inst = this.backends[backend];
    if (!inst) throw new Error(`Backend '${backend}' not available`);
    return inst;
  }

  /**
   * 存储数据
   * @param {string} key - 键值
   * @param {*} value - 要存储的值
   * @param {Object} opts - 选项
   * @param {string} opts.backend - 指定后端
   * @param {string} opts.contentType - 内容类型
   */
  async put(key, value, opts) {
    const backend = (opts && opts.backend) || this.defaultBackend;
    return this._pick(backend).put(key, value, opts && opts.contentType);
  }

  /**
   * 获取数据
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @param {string} opts.as - 返回格式 ('text'、'json'、'blob' 或 'response')
   * @param {string} opts.backend - 指定后端
   * @returns {Promise<*>} 返回指定格式的数据
   */
  async get(key, opts) {
    const as = (opts && opts.as) || "text";
    if (opts && opts.backend) return this._pick(opts.backend).get(key, as);
    for (const b of this.readOrder) {
      if (!this.backends[b]) continue;
      const v = await this.backends[b].get(key, as);
      if (v !== undefined) return v;
    }
  }

  /**
   * 检查键值是否存在
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @param {string} opts.backend - 指定后端
   * @returns {Promise<boolean>} 是否存在
   */
  async has(key, opts) {
    if (opts && opts.backend) return this._pick(opts.backend).has(key);
    for (const b of this.readOrder) if (await this.backends[b].has(key)) return true;
    return false;
  }

  /**
   * 删除数据
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @param {string} opts.backend - 指定后端
   * @returns {Promise<boolean>} 是否删除成功
   */
  async delete(key, opts) {
    if (opts && opts.backend) return this._pick(opts.backend).delete(key);
    let removed = false;
    for (const b of Object.keys(this.backends)) removed = (await this.backends[b].delete(key)) || removed;
    return removed;
  }

  /**
   * 列出所有键值
   * @param {Object} opts - 选项
   * @param {string} opts.backend - 指定后端
   * @returns {Promise<string[]>} 键值列表
   */
  async list(opts) {
    if (opts && opts.backend) return this._pick(opts.backend).list();
    const sets = await Promise.all(Object.values(this.backends).map(b => b.list().catch(() => [])));
    return [...new Set(sets.flat())];
  }

  /**
   * 清空所有数据
   * @param {Object} opts - 选项
   * @param {string} opts.backend - 指定后端
   */
  async clear(opts) {
    if (opts && opts.backend) return this._pick(opts.backend).clear();
    await Promise.all(Object.values(this.backends).map(b => b.clear().catch(() => {
    })));
  }

  /**
   * 存储文本数据
   * @param {string} key - 键值
   * @param {string} text - 文本内容
   * @param {Object} opts - 选项
   */
  async putText(key, text, opts) {
    return this.put(key, text, { ...(opts || {}), contentType: "text/plain;charset=utf-8" });
  }

  /**
   * 获取文本数据
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @returns {Promise<string>} 文本内容
   */
  async getText(key, opts) {
    return this.get(key, { ...(opts || {}), as: "text" });
  }

  /**
   * 存储 JSON 数据
   * @param {string} key - 键值
   * @param {*} data - JSON 数据
   * @param {Object} opts - 选项
   */
  async putJSON(key, data, opts) {
    return this.put(key, data, { ...(opts || {}), contentType: "application/json" });
  }

  /**
   * 获取 JSON 数据
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @returns {Promise<*>} JSON 数据
   */
  async getJSON(key, opts) {
    return this.get(key, { ...(opts || {}), as: "json" });
  }

  /**
   * 存储 Blob 数据
   * @param {string} key - 键值
   * @param {Blob} blob - Blob 对象
   * @param {Object} opts - 选项
   */
  async putBlob(key, blob, opts) {
    return this.put(key, blob, { ...(opts || {}), contentType: blob.type });
  }

  /**
   * 获取 Blob 数据
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @returns {Promise<Blob>} Blob 对象
   */
  async getBlob(key, opts) {
    return this.get(key, { ...(opts || {}), as: "blob" });
  }

  /**
   * 存储 Response 对象
   * @param {string} key - 键值
   * @param {Response} res - Response 对象
   * @param {Object} opts - 选项
   */
  async putResponse(key, res, opts) {
    const ct = res.headers.get("Content-Type");
    const blob = await res.blob();
    return this.put(key, blob, { ...(opts || {}), contentType: ct });
  }

  /**
   * 获取 Response 对象
   * @param {string} key - 键值
   * @param {Object} opts - 选项
   * @returns {Promise<Response>} Response 对象
   */
  async getResponse(key, opts) {
    return this.get(key, { ...(opts || {}), as: "response" });
  }

  /**
   * 检查浏览器支持情况
   * @returns {{cache: boolean, idb: boolean}} 支持情况
   */
  static supports() {
    return { cache: isCacheAPISupported(), idb: isIDBSupported() };
  }
}

// 将类暴露到全局 window 对象（如果存在）
try {
  if (typeof window !== "undefined") window.CacheFileStore = CacheFileStore;
} catch (_) {
}
